import {Trans} from 'react-i18next'
import messages from '../../assets/messages'
import {PammOfferType} from '../../graphql/types'
import {MlmInfoBanner} from './components/MlmInfoBanner'
import {NetworkKpiCards} from './components/NetworkKpiCards'
import {NetworkLegend} from './components/NetworkLegend'
import {OfferTreeNode} from './components/OfferTreeNode'
import {EmptyState, Root, ScrollArea, ScrollHint, TreeInner} from './PammOffersTree.styled'
import {buildOfferTree} from './utils/buildOfferTree'
import {aggregateNetworkStats} from './utils/networkStats'

type PammOffersTreeProps = {
  offers: PammOfferType[]
}

/**
 * Hierarchical, expandable tree view of PAMM offers (PT-5399).
 *
 * Rebuilds the parent/child hierarchy from each offer's agent chain and renders it as
 * nested, collapsible cards, preceded by the network KPI summary, a colour legend and an
 * explanatory banner. Used by both the Money Manager and Agent offer pages.
 *
 * @param offers - Flat list of PAMM offers in the viewer's scope; order does not matter.
 */
export const PammOffersTree = ({offers}: PammOffersTreeProps) => {
  const roots = buildOfferTree(offers)
  const stats = aggregateNetworkStats(roots)
  const currency = offers.find((offer) => offer.currency)?.currency ?? 'USD'

  if (roots.length === 0) {
    return (
      <EmptyState data-testid="pamm-offers-tree-empty">
        <Trans {...messages.pammNetworkEmpty} />
      </EmptyState>
    )
  }

  return (
    <Root>
      <NetworkKpiCards stats={stats} currency={currency} />
      <MlmInfoBanner />
      <NetworkLegend />
      <ScrollHint>
        <Trans {...messages.pammScrollHint} />
      </ScrollHint>
      <ScrollArea>
        <TreeInner>
          {roots.map((root, index) => (
            <OfferTreeNode key={root.offer.id ?? index} node={root} />
          ))}
        </TreeInner>
      </ScrollArea>
    </Root>
  )
}
